import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { restoreSession, useSession } from './lib/session';
import { AuthView } from './views/AuthView';
import { Layout } from './views/Layout';
import { InviteView } from './views/InviteView';
import { AndroidDownload } from './views/AndroidDownload';
import { Spinner } from './components/ui';

export function App() {
  const status = useSession((s) => s.status);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    void restoreSession();
  }, []);
  // Clicking a push notification focuses this tab and asks it to open the conversation.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent<{ type?: string; url?: string }>) => {
      if (e.data?.type === 'navigate' && e.data.url?.startsWith('/')) navigate(e.data.url);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  if (status === 'loading') {
    return (
      <div className="flex h-full items-center justify-center bg-bg-0">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (status === 'offline') {
    return (
      <div className="safe-y flex h-full flex-col items-center justify-center gap-3 bg-bg-0 p-6 text-center">
        <Spinner className="h-8 w-8" />
        <h1 className="text-lg font-semibold text-text-0">You're offline</h1>
        <p className="max-w-xs text-sm text-text-3">
          Comms will reconnect as soon as this device can reach the server again.
        </p>
      </div>
    );
  }

  if (status === 'anonymous') {
    const invite = location.pathname.match(/^\/invite\/([\w-]+)/)?.[1];
    return (
      <Routes>
        <Route path="/login" element={<AuthView mode="login" />} />
        <Route path="/register" element={<AuthView mode="register" />} />
        <Route path="/android" element={<AndroidDownload />} />
        <Route
          path="*"
          element={<Navigate to={invite ? `/register?invite=${invite}` : '/login'} replace />}
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/invite/:code" element={<InviteView />} />
      <Route path="/android" element={<AndroidDownload />} />
      <Route path="/channels/:guildId/:channelId?" element={<Layout />} />
      <Route path="/admin" element={<Layout admin />} />
      <Route path="*" element={<Navigate to="/channels/@me" replace />} />
    </Routes>
  );
}
